import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  BottomSheet,
  Button,
  Dialog,
  IconButton,
  List,
  ListItem,
  Menu,
  Notice,
  TextField,
  iconMore,
  useSnackbar,
} from '@sauda/ui/kit';
import { useAuth } from '../../auth/AuthContext.js';
import { useChrome } from '../../app/chrome.js';
import { ApiError, api } from '../../lib/api.js';
import type { DealDetailView } from '../../lib/types.js';
import { FINISHED } from './stages.js';

type Sheet = 'phone' | 'reassign' | null;
type Confirm = 'lost' | 'cancelled' | null;

/** The deal's overflow menu: change the client's number, hand it to someone else, mark it lost or cancelled. */
export function DealMenu({ deal }: { deal: DealDetailView }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const chrome = useChrome();
  const cache = useQueryClient();
  const snack = useSnackbar();
  const navigate = useNavigate();
  const [sheet, setSheet] = useState<Sheet>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const finished = FINISHED.includes(deal.state);
  const canReassign = auth.allowed('records.reassign');
  const members = useQuery({
    queryKey: ['assignable'],
    queryFn: () => api<{ id: string; name: string | null; role: string }[]>('/visits/assignable'),
    enabled: sheet === 'reassign',
  });
  const refresh = () =>
    Promise.all([
      cache.invalidateQueries({ queryKey: ['deal', deal.id] }),
      cache.invalidateQueries({ queryKey: ['deals'] }),
      cache.invalidateQueries({ queryKey: ['today'] }),
    ]);

  const changePhone = useMutation({
    mutationFn: () => api(`/deals/${deal.id}/client-phone`, { method: 'PATCH', body: { phone } }),
    onSuccess: async () => {
      setSheet(null);
      setPhone('');
      await Promise.all([refresh(), cache.invalidateQueries({ queryKey: ['memo', deal.id] })]);
      snack({ text: t('deal.phoneChanged') });
    },
    onError: (e) =>
      setError(
        e instanceof ApiError && e.status < 500 && e.message !== '' ? e.message : t('deal.generic'),
      ),
  });
  const reassign = useMutation({
    mutationFn: (memberId: string) =>
      api(`/deals/${deal.id}/reassign`, { method: 'POST', body: { memberId } }),
    onSuccess: async () => {
      setSheet(null);
      await refresh();
      snack({ text: t('deal.reassigned') });
    },
    onError: () => setError(t('deal.generic')),
  });
  const end = useMutation({
    mutationFn: (to: 'lost' | 'cancelled') =>
      api(`/deals/${deal.id}/transition`, { method: 'POST', body: { to } }),
    onSuccess: async () => {
      setConfirm(null);
      await refresh();
      void navigate('/deals');
    },
    onError: () => {
      setConfirm(null);
      snack({ text: t('deal.generic') });
    },
  });

  if (finished) return null;
  const items = [
    {
      key: 'phone',
      label: t('deal.changePhone'),
      onSelect: () => {
        setError(null);
        setSheet('phone');
      },
    },
    ...(canReassign
      ? [
          {
            key: 'reassign',
            label: t('deal.reassign'),
            onSelect: () => {
              setError(null);
              setSheet('reassign');
            },
          },
        ]
      : []),
    ...(deal.state === 'registered'
      ? []
      : [
          {
            key: 'lost',
            label: t('deals.markLost'),
            destructive: true,
            onSelect: () => setConfirm('lost' as const),
          },
          {
            key: 'cancelled',
            label: t('deals.cancelDeal'),
            destructive: true,
            onSelect: () => setConfirm('cancelled' as const),
          },
        ]),
  ];
  return (
    <>
      <Menu
        label={t('deal.menu')}
        items={items}
        trigger={(p) => (
          <IconButton {...p} icon={iconMore} label={t('deal.menu')} disabled={chrome.readOnly} />
        )}
      />
      <BottomSheet
        open={sheet === 'phone'}
        onClose={() => setSheet(null)}
        label={t('deal.changePhone')}
        title={t('deal.changePhone')}
        closeLabel={t('app.close')}
        actions={
          <Button
            size="lg"
            block
            loading={changePhone.isPending}
            disabled={phone.replace(/\D/g, '').length < 10}
            onClick={() => changePhone.mutate()}
          >
            {t('app.save')}
          </Button>
        }
      >
        <div className="k-stack">
          <Notice tone="warning">{t('deal.phoneWarning')}</Notice>
          <TextField
            label={t('deal.newPhone')}
            type="tel"
            inputMode="numeric"
            prefix="+91"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          {error === null ? null : <Notice tone="error">{error}</Notice>}
        </div>
      </BottomSheet>
      <BottomSheet
        open={sheet === 'reassign'}
        onClose={() => setSheet(null)}
        label={t('deal.reassign')}
        title={t('deal.reassign')}
        closeLabel={t('app.close')}
      >
        {error === null ? null : <Notice tone="error">{error}</Notice>}
        <List label={t('deal.reassign')}>
          {(members.data ?? [])
            .filter((m) => m.id !== deal.agent.memberId)
            .map((m) => (
              <ListItem
                key={m.id}
                title={m.name ?? t('today.unnamed')}
                supporting={t(`roles.${m.role}`)}
                onClick={() => reassign.mutate(m.id)}
              />
            ))}
        </List>
      </BottomSheet>
      <Dialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        title={confirm === 'lost' ? t('deal.lostTitle') : t('deal.cancelTitle')}
        destructive
        actions={
          <>
            <Button variant="text" onClick={() => setConfirm(null)}>
              {t('app.cancel')}
            </Button>
            <Button
              destructive
              loading={end.isPending}
              onClick={() => confirm !== null && end.mutate(confirm)}
            >
              {confirm === 'lost' ? t('deals.markLost') : t('deals.cancelDeal')}
            </Button>
          </>
        }
      >
        {t('deal.endBody')}
      </Dialog>
    </>
  );
}
